import React, {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Select from 'react-select'
import {aiInterpApi} from './api'
import type {AllowedUserDto} from './types'
import {BulkImportModal} from './BulkImportModal'
import {useAllAccountsForFilter} from '../../hooks/useGlobalData'

type UserOption = {value: string; label: string; email: string}

type EditLimitsModalProps = {
  row: AllowedUserDto | null
  onClose: () => void
  onSaved: (updated: AllowedUserDto) => void
}

const EditLimitsModal: React.FC<EditLimitsModalProps> = ({row, onClose, onSaved}) => {
  const [maxAttempts, setMaxAttempts] = useState('')
  const [maxDurationMinutes, setMaxDurationMinutes] = useState('')
  const [requiredFeedback, setRequiredFeedback] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (row) {
      setMaxAttempts(String(row.maxAttempts))
      setMaxDurationMinutes(String(row.maxDurationMinutes))
      setRequiredFeedback(row.requiredFeedback)
      setError(null)
    }
  }, [row])

  if (!row) return null

  const parsedAttempts = Number(maxAttempts)
  const parsedDuration = Number(maxDurationMinutes)
  const attemptsValid = Number.isFinite(parsedAttempts) && parsedAttempts >= 0 && parsedAttempts <= 999
  const durationValid = Number.isFinite(parsedDuration) && parsedDuration >= 1 && parsedDuration <= 120
  const canSave = attemptsValid && durationValid && !busy

  const save = async () => {
    if (!canSave) return
    setBusy(true); setError(null)
    try {
      const updated = await aiInterpApi.updateAllowed(row.id, {
        maxAttempts: Math.trunc(parsedAttempts),
        maxDurationMinutes: Math.trunc(parsedDuration),
        requiredFeedback,
      })
      onSaved(updated)
      onClose()
    } catch (e: any) {
      setError(String(e?.response?.data?.error ?? e?.message ?? e))
    } finally {
      setBusy(false)
    }
  }

  const wouldExhaust = attemptsValid && Math.trunc(parsedAttempts) <= row.totalAttempts

  return (
    <Modal show={true} onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Edit limits — {row.fullName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-3'>
          <label className='form-label fw-semibold text-muted text-uppercase fs-8 mb-1'>Max Attempts</label>
          <input
            type='number'
            className={`form-control ${attemptsValid ? '' : 'is-invalid'}`}
            value={maxAttempts}
            min={0}
            max={999}
            onChange={e => setMaxAttempts(e.target.value)}
            disabled={busy}
          />
          <div className='form-text'>
            Lifetime cap. Current usage: {row.totalAttempts} attempts.
            {wouldExhaust && Math.trunc(parsedAttempts) < row.totalAttempts && (
              <span className='text-danger ms-1'>Setting below {row.totalAttempts} will permanently lock this user out.</span>
            )}
          </div>
        </div>
        <div className='mb-3'>
          <label className='form-label fw-semibold text-muted text-uppercase fs-8 mb-1'>Max Duration (minutes per call)</label>
          <input
            type='number'
            className={`form-control ${durationValid ? '' : 'is-invalid'}`}
            value={maxDurationMinutes}
            min={1}
            max={120}
            onChange={e => setMaxDurationMinutes(e.target.value)}
            disabled={busy}
          />
          <div className='form-text'>Hard cap on each call (1–120 minutes).</div>
        </div>
        <div className='form-check form-switch mb-1'>
          <input
            className='form-check-input'
            type='checkbox'
            role='switch'
            id='edit-required-feedback'
            checked={requiredFeedback}
            onChange={e => setRequiredFeedback(e.target.checked)}
            disabled={busy}
          />
          <label className='form-check-label fw-semibold' htmlFor='edit-required-feedback'>
            Feedback Required
          </label>
          <div className='form-text'>
            When off, users can use AI interpretation without submitting post-call feedback.
          </div>
        </div>
        {error && <div className='alert alert-danger py-2 mt-3 mb-0'>{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-sm btn-light' onClick={onClose} disabled={busy}>Cancel</button>
        <button type='button' className='btn btn-sm btn-primary' onClick={save} disabled={!canSave}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export const AIInterpretationConfigPage: React.FC = () => {
  const [rows, setRows] = useState<AllowedUserDto[]>([])
  const [selected, setSelected] = useState<UserOption | null>(null)
  const [languages, setLanguages] = useState('')
  const [requiredFeedback, setRequiredFeedback] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<AllowedUserDto | null>(null)

  const {data: accountsData, isLoading: accountsLoading} = useAllAccountsForFilter()

  const userOptions = useMemo<UserOption[]>(() => {
    const taken = new Set(rows.map(r => r.email.toLowerCase()))
    return (accountsData ?? [])
      .map((d: any) => {
        const display = String(d.value ?? '')
        const dashIdx = display.lastIndexOf(' - ')
        const email = dashIdx >= 0 ? display.slice(dashIdx + 3).trim() : ''
        return {value: String(d.key ?? ''), label: display, email}
      })
      .filter(o => o.email && !taken.has(o.email.toLowerCase()))
  }, [accountsData, rows])

  const reload = async () => {
    setLoading(true)
    try { setRows(await aiInterpApi.listAllowed()); setError(null) }
    catch (e: any) { setError(String(e?.message ?? e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { reload() }, [])

  const add = async () => {
    if (!selected) { setError('Please select a user.'); return }
    try {
      await aiInterpApi.addAllowed({
        email: selected.email,
        languages: languages.trim() || null,
        requiredFeedback,
      })
      setSelected(null); setLanguages(''); setRequiredFeedback(true)
      await reload()
    } catch (e: any) {
      setError(e?.response?.status === 404
        ? 'User not found.'
        : String(e?.message ?? e))
    }
  }

  const remove = async (id: number, name: string) => {
    if (!window.confirm(`Remove ${name} from the AI Interpretation allow-list?`)) return
    try {
      await aiInterpApi.removeAllowed(id)
      await reload()
    } catch (e: any) {
      setError(String(e?.message ?? e))
    }
  }

  const applyRowUpdate = (updated: AllowedUserDto) => {
    setRows(prev => prev.map(r => r.id === updated.id ? updated : r))
  }

  return (
    <div className='p-4'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <div>
          <h3 className='mb-1'>AI Interpretation — Allowed Users</h3>
          <div className='text-muted fs-7'>Users granted access to the AI interpretation widget.</div>
        </div>
        <button className='btn btn-sm btn-primary' onClick={() => setBulkOpen(true)}>
          <i className='bi bi-upload me-2' />Bulk Import
        </button>
      </div>

      <div className='card p-4 mb-4'>
        <div className='row g-3 align-items-end'>
          <div className='col-md-4'>
            <label className='form-label fw-semibold text-muted text-uppercase fs-8 mb-1'>User</label>
            <Select<UserOption>
              classNamePrefix='react-select'
              options={userOptions}
              value={selected}
              onChange={(opt: UserOption | null) => setSelected(opt ?? null)}
              isClearable
              isLoading={accountsLoading}
              placeholder='Search user by name or email…'
              noOptionsMessage={() =>
                accountsLoading ? 'Loading…' : 'No users available'
              }
            />
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-semibold text-muted text-uppercase fs-8 mb-1'>Languages (optional)</label>
            <input
              className='form-control'
              value={languages}
              onChange={e => setLanguages(e.target.value)}
              placeholder='e.g. Spanish; Portuguese'
            />
          </div>
          <div className='col-md-2'>
            <label className='form-label fw-semibold text-muted text-uppercase fs-8 mb-1 invisible'>
              Feedback
            </label>
            <div className='d-flex justify-content-center align-items-center pb-3'>
              <div className='form-check form-switch m-0'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  role='switch'
                  id='add-required-feedback'
                  checked={requiredFeedback}
                  onChange={e => setRequiredFeedback(e.target.checked)}
                  disabled={!selected}
                />
                <label
                  className={`form-check-label fw-semibold ${!selected ? 'text-muted' : ''}`}
                  htmlFor='add-required-feedback'
                >
                  Feedback Required
                </label>
              </div>
            </div>
          </div>
          <div className='col-md-3'>
            <button
              className='btn btn-primary w-100'
              onClick={add}
              disabled={!selected}
            >
              Add to allow-list
            </button>
          </div>
        </div>
        {error && <div className='alert alert-danger py-2 mt-3 mb-0'>{error}</div>}
      </div>

      <div className='card p-4'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 table-hover align-middle gs-2 gy-3 mb-0'>
            <thead>
              <tr className='fw-bold text-muted text-uppercase fs-8'>
                <th>Name</th>
                <th>Email</th>
                <th>Languages</th>
                <th className='text-center'>Total Attempts</th>
                <th className='text-center'>Max Attempts</th>
                <th className='text-center'>Max Duration (min)</th>
                <th className='text-center'>Feedback Required</th>
                <th className='text-center'>Pending Feedback</th>
                <th>Added</th>
                <th className='text-end'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className='text-center text-muted py-4'>Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className='text-center text-muted py-4'>No allowed users yet.</td></tr>
              )}
              {!loading && rows.map(r => {
                const exhausted = r.totalAttempts >= r.maxAttempts
                return (
                  <tr key={r.id}>
                    <td className='fw-semibold'>{r.fullName}</td>
                    <td>{r.email}</td>
                    <td>{r.languages ?? '—'}</td>
                    <td className='text-center'>
                      <span className={exhausted ? 'badge badge-light-danger' : 'badge badge-light-info'}>
                        {r.totalAttempts} / {r.maxAttempts}
                      </span>
                    </td>
                    <td className='text-center'>{r.maxAttempts}</td>
                    <td className='text-center'>{r.maxDurationMinutes}</td>
                    <td className='text-center'>
                      {r.requiredFeedback
                        ? <span className='badge badge-light-primary'>Yes</span>
                        : <span className='badge badge-light-secondary'>No</span>}
                    </td>
                    <td className='text-center'>
                      {r.hasPendingFeedback
                        ? <span className='badge badge-light-warning'>Pending</span>
                        : <span className='badge badge-light-success'>Clear</span>}
                    </td>
                    <td>{new Date(r.createdAtUtc).toLocaleDateString()}</td>
                    <td className='text-end'>
                      <div className='d-inline-flex gap-2 justify-content-end'>
                        <button
                          className='btn btn-sm btn-icon btn-light-primary'
                          aria-label={`Edit limits for ${r.fullName}`}
                          title='Edit limits'
                          onClick={() => setEditingRow(r)}
                        >
                          <i className='bi bi-pencil' />
                        </button>
                        <button
                          className='btn btn-sm btn-icon btn-light-danger'
                          aria-label={`Remove ${r.fullName}`}
                          title='Remove from allow-list'
                          onClick={() => remove(r.id, r.fullName)}
                        >
                          <i className='bi bi-trash' />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <BulkImportModal open={bulkOpen} onClose={() => setBulkOpen(false)} onComplete={reload} />
      <EditLimitsModal
        row={editingRow}
        onClose={() => setEditingRow(null)}
        onSaved={applyRowUpdate}
      />
    </div>
  )
}
